import type { ActionButtonColors } from 'atoms/ActionButton/ActionButton.h'
import type { IconNames } from 'atoms/Icon/Icon.h'
import type { INDENTS } from 'vars/index'

import { COLOR } from 'vars/index'

export const IconPositions = {
  Left: 'Left',
  Right: 'Right',
  Center: 'Center',
} as const
export type IconPositions = (typeof IconPositions)[keyof typeof IconPositions]

export const ButtonIconSizes = {
  small: 'small',
  medium: 'medium',
  xMedium: 'xMedium',
  xxMedium: 'xxMedium',
  big: 'big',
  sm: 'sm',
  mSm: 'mSm',
  xSm: 'xSm',
  mLarge: 'mLarge',
} as const
export type ButtonIconSizes =
  (typeof ButtonIconSizes)[keyof typeof ButtonIconSizes]

export const ButtonFonts = {
  Button1: 'Button1',
  Button2: 'Button2',
  Button3: 'Button3',
  Caption4: 'Caption4',
  Body3: 'Body3',
  Body4: 'Body4',
  Font4xl: 'Font4xl',
  Font3xl: 'Font3xl',
  Font2xl: 'Font2xl',
  FontXl: 'FontXl',
  FontLg: 'FontLg',
  FontLgBold: 'FontLgBold',
  FontSm: 'FontSm',
  FontSmBold: 'FontSmBold',
  FontXs: 'FontXs',
} as const
export type ButtonFonts = (typeof ButtonFonts)[keyof typeof ButtonFonts]

export const ButtonCorners = {
  CORNERS_0: 'CORNERS_0',
  CORNERS_5: 'CORNERS_5',
  CORNERS_10: 'CORNERS_10',
  CORNERS_20: 'CORNERS_20',
  CORNERS_25: 'CORNERS_25',
  CORNERS_30: 'CORNERS_30',
  CORNERS_50: 'CORNERS_50',
  CORNERS_PILL: 'CORNERS_PILL',
  CORNERS_ROUND: 'CORNERS_ROUND',
  CORNERS_RIGHT_25: 'CORNERS_RIGHT_25',
  CORNERS_LEFT_25: 'CORNERS_LEFT_25',
} as const
export type ButtonCorners = (typeof ButtonCorners)[keyof typeof ButtonCorners]

export const ButtonBordersWidth = {
  BORDER_0: 'BORDER_0',
  BORDER_10: 'BORDER_10',
  BORDER_20: 'BORDER_20',
  BORDER_30: 'BORDER_30',
} as const
export type ButtonBordersWidth =
  (typeof ButtonBordersWidth)[keyof typeof ButtonBordersWidth]

export const ButtonBorderColors = {
  COLOR_ACCENT_TERTIARY: COLOR.ACCENT_TERTIARY,
  COLOR_ACCENT_SECONDARY: COLOR.ACCENT_SECONDARY,
  COLOR_ACCENT_PRIMARY_PRESSED: COLOR.ACCENT_PRIMARY_PRESSED,
  COLOR_ACCENT_DISABLED: COLOR.DISABLED,
  COLOR_BASE_DEFAULT: COLOR.BASE_MAIN_DEFAULT,
  SURFACE_LIGHT_2: COLOR.SURFACE_LIGHT_2,
  COLOR_BASE_PRESSED: COLOR.BASE_MAIN_PRESSED,
  COLOR_DISABLED: COLOR.DISABLED,
} as const
export type ButtonBorderColors =
  (typeof ButtonBorderColors)[keyof typeof ButtonBorderColors]

export const ButtonColors = {
  COLOR_PRIMARY: COLOR.TEXT_ICON_PRIMARY,
  COLOR_SECONDARY: COLOR.TEXT_ICON_CONSTANT_DARK,
  COLOR_TEXT_SECONDARY: COLOR.TEXT_ICON_SECONDARY,
  COLOR_TEXT_TERTIARY: COLOR.TEXT_ICON_TERTIARY,
  ACCENT_TERTIARY: COLOR.ACCENT_TERTIARY,
  COLOR_DISABLED: COLOR.TEXT_ICON_TERTIARY,
  COLOR_MAIN_DEFAULT: COLOR.BASE_MAIN_DEFAULT,
  SURFACE_LIGHT_2: COLOR.SURFACE_LIGHT_2,
  COLOR_MAIN_PRESSED: COLOR.BASE_MAIN_PRESSED,
} as const
export type ButtonColors = (typeof ButtonColors)[keyof typeof ButtonColors]

export const BUTTON_PADDINGS = {
  SIZE_10: '--button-paddings-size-10',
  SIZE_20: '--button-paddings-size-20',
} as const
export type BUTTON_PADDINGS =
  (typeof BUTTON_PADDINGS)[keyof typeof BUTTON_PADDINGS]

export const ButtonFluid = {
  Vertical: 'vertical',
  Horizontal: 'horizontal',
  Both: 'both',
  None: 'none',
} as const
export type ButtonFluid = (typeof ButtonFluid)[keyof typeof ButtonFluid]

export const ButtonLoaderSizes = {
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const
export type ButtonLoaderSizes =
  (typeof ButtonLoaderSizes)[keyof typeof ButtonLoaderSizes]

export const loaderBoxSizeMap = {
  [ButtonLoaderSizes.small]: 20,
  [ButtonLoaderSizes.medium]: 24,
  [ButtonLoaderSizes.large]: 28,
}

export const loaderInsetMap = {
  [ButtonLoaderSizes.small]: 4,
  [ButtonLoaderSizes.medium]: 4,
  [ButtonLoaderSizes.large]: 5,
}

export const loaderStrokeMap = {
  [ButtonLoaderSizes.small]: 1.5,
  [ButtonLoaderSizes.medium]: 2,
  [ButtonLoaderSizes.large]: 2,
}

export const loaderFootprintMap = {
  [ButtonLoaderSizes.small]: 28,
  [ButtonLoaderSizes.medium]: 32,
  [ButtonLoaderSizes.large]: 36,
}

export interface IButtonProps {
  type?: 'button' | 'submit' | 'reset'
  font: ButtonFonts
  title?: string
  icon?: IconNames
  iconPosition?: IconPositions
  iconSize?: (typeof ButtonIconSizes)['small' | 'medium']
  colorDefault: ButtonColors | ActionButtonColors
  colorHovered?: ButtonColors | ActionButtonColors
  colorPressed?: ButtonColors | ActionButtonColors
  colorDisabled?: ButtonColors | ActionButtonColors
  backgroundColorDefault: COLOR
  backgroundColorHovered?: COLOR
  backgroundColorPressed?: COLOR
  backgroundColorDisabled?: COLOR
  gradient?: COLOR
  paddings?: INDENTS[]
  corners?: ButtonCorners
  borderWidth: ButtonBordersWidth
  bordersColorDefault?: ButtonBorderColors
  bordersColorHovered?: ButtonBorderColors
  bordersColorPressed?: ButtonBorderColors
  bordersColorDisabled?: ButtonBorderColors
  fluid?: ButtonFluid
  className?: 'hovered' | 'pressed'
  disabled?: boolean
  withLoader?: boolean
  hideTitleOnLoading?: boolean
  loaderSize?: ButtonLoaderSizes
}

export interface IButtonTemplate {
  title?: string
  disabled?: boolean
  paddings?: IButtonProps['paddings']
}
